/* Shared React hook that manages the user's notification state:
   - REST-loaded list + unread count (initial + on-demand refresh)
   - SSE subscription for live pushes (bell badge + toast)
   - Optimistic mutations for mark-read / archive / delete

   Consumed by <NotificationBell/>, <NotificationToaster/>, and the Notification Center page.
   Kept in a tiny context so both the bell and the toaster share a single SSE connection. */

import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NotifCtx = createContext(null);

export function NotificationProvider({ authed, children }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [toasts, setToasts] = useState([]);        // live-only queue for <NotificationToaster/>
  const seenIds = useRef(new Set());
  const esRef = useRef(null);

  const load = useCallback(async () => {
    if (!authed) return;
    try {
      const [list, count] = await Promise.all([
        axios.get(`${API}/notifications?limit=50`),
        axios.get(`${API}/notifications/unread-count`),
      ]);
      const arr = list.data.notifications || [];
      setItems(arr);
      arr.forEach((n) => seenIds.current.add(n.id));
      setUnread(count.data.count || 0);
    } catch { /* ignored — bell + center degrade gracefully */ }
  }, [authed]);

  useEffect(() => { load(); }, [load]);

  // SSE for live updates
  useEffect(() => {
    if (!authed) return;
    // EventSource can't attach auth headers; the backend accepts either the session cookie
    // (already set on browser) or a Bearer via header. Cookie-based auth works here.
    const url = `${API}/notifications/stream`;
    let es;
    try {
      es = new EventSource(url, { withCredentials: true });
      esRef.current = es;
    } catch { return; }

    es.addEventListener("hello", (evt) => {
      try {
        const { unread: u } = JSON.parse(evt.data);
        if (typeof u === "number") setUnread(u);
      } catch { /* noop */ }
    });
    es.addEventListener("notification", (evt) => {
      try {
        const n = JSON.parse(evt.data);
        if (seenIds.current.has(n.id)) return;
        seenIds.current.add(n.id);
        setItems((prev) => [n, ...prev].slice(0, 100));
        setUnread((u) => u + 1);
        setToasts((prev) => [...prev, n]);
      } catch { /* noop */ }
    });
    es.onerror = () => { /* browser auto-reconnects; nothing to do */ };
    return () => { try { es.close(); } catch { /* noop */ } };
  }, [authed]);

  const markRead = useCallback(async (id) => {
    setItems((p) => p.map((n) => n.id === id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n));
    setUnread((u) => Math.max(0, u - 1));
    try { await axios.post(`${API}/notifications/${id}/read`); } catch { /* noop */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setItems((p) => p.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    setUnread(0);
    try { await axios.post(`${API}/notifications/read-all`); } catch { /* noop */ }
  }, []);

  const archive = useCallback(async (id) => {
    setItems((p) => p.filter((n) => n.id !== id));
    try { await axios.post(`${API}/notifications/${id}/archive`); } catch { /* noop */ }
  }, []);

  const remove = useCallback(async (id) => {
    setItems((p) => p.filter((n) => n.id !== id));
    try { await axios.delete(`${API}/notifications/${id}`); } catch { /* noop */ }
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((p) => p.filter((n) => n.id !== id));
  }, []);

  return (
    <NotifCtx.Provider value={{
      items, unread, toasts,
      load, markRead, markAllRead, archive, remove, dismissToast,
    }}>{children}</NotifCtx.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotifCtx);
  if (!ctx) {
    // Safe default when the provider isn't mounted (e.g. shared/public pages).
    return {
      items: [], unread: 0, toasts: [],
      load: () => {}, markRead: () => {}, markAllRead: () => {},
      archive: () => {}, remove: () => {}, dismissToast: () => {},
    };
  }
  return ctx;
}
